import { HttpClient } from '@angular/common/http';
import * as i0 from '@angular/core';
import { inject, Injectable } from '@angular/core';
import { COST_ESTIMATOR_WEB_SERVICE } from '@effy-tech/angular-common/web-service';
import { of } from 'rxjs';

var CostEstimatorKeys;
(function (CostEstimatorKeys) {
    CostEstimatorKeys["AccommodationBathroomsCount"] = "accommodation_bathrooms_count";
    CostEstimatorKeys["AccommodationType"] = "accommodation_type";
    CostEstimatorKeys["ApartmentsCount"] = "apartments_count";
    CostEstimatorKeys["AtticsType"] = "attics_type";
    CostEstimatorKeys["BoilerLocation"] = "boiler_location";
    CostEstimatorKeys["BoilerType"] = "boiler_type";
    CostEstimatorKeys["CoConcentration"] = "co_concentration";
    CostEstimatorKeys["CommitmentDate"] = "commitment_date";
    CostEstimatorKeys["DocStampDate"] = "docstamp_date";
    CostEstimatorKeys["CreationDate"] = "creation_date";
    CostEstimatorKeys["DpeScore"] = "dpe_score";
    CostEstimatorKeys["EmittersCount"] = "emitters_count";
    CostEstimatorKeys["EmitterCertification"] = "emitter_certification";
    CostEstimatorKeys["EnergyEfficiency"] = "energy_efficiency";
    CostEstimatorKeys["EquipmentCertification"] = "equipment_certification";
    CostEstimatorKeys["ExistingGlazingMaterial"] = "existing_glazing_material";
    CostEstimatorKeys["ExistingHeatingMode"] = "existing_heating_mode";
    CostEstimatorKeys["ExistingHeatingType"] = "existing_heating_type";
    CostEstimatorKeys["ExpectedDpeScore"] = "expected_dpe_score";
    CostEstimatorKeys["HeatedSurface"] = "heated_surface";
    CostEstimatorKeys["HeatingEnergy"] = "heating_energy";
    CostEstimatorKeys["HeatPumpType"] = "heat_pump_type";
    CostEstimatorKeys["HousingAge"] = "housing_age";
    CostEstimatorKeys["InsulatingSurface"] = "insulating_surface";
    CostEstimatorKeys["LivingSurface"] = "living_surface";
    CostEstimatorKeys["MprSubmissionDate"] = "mpr_submission_date";
    CostEstimatorKeys["NoxEmission"] = "nox_emission";
    CostEstimatorKeys["OfferType"] = "offer_type";
    CostEstimatorKeys["OwnerStatus"] = "owner_status";
    CostEstimatorKeys["ParticleEmission"] = "particle_emission";
    CostEstimatorKeys["PrecarityLevel"] = "precarity_level";
    CostEstimatorKeys["PriorVisitDate"] = "prior_visit_date";
    CostEstimatorKeys["StoveType"] = "stove_type";
    CostEstimatorKeys["SwSolarFactor"] = "sw_solar_factor";
    CostEstimatorKeys["ThermalResistance"] = "thermal_resistance";
    CostEstimatorKeys["UwCoefficient"] = "uw_coefficient";
    CostEstimatorKeys["WallInsulationType"] = "wall_insulation_type";
    CostEstimatorKeys["WallInsulationWorkprojectInsulationType"] = "wall_insulation_workproject_insulation_type";
    CostEstimatorKeys["WindowMaterial"] = "window_material";
    CostEstimatorKeys["WindowType"] = "window_type";
    CostEstimatorKeys["WindowSensor"] = "window_sensor";
    CostEstimatorKeys["WindowsCount"] = "windows_count";
    CostEstimatorKeys["WithEcs"] = "with_ecs";
    CostEstimatorKeys["WorkStartDate"] = "work_start_date";
    CostEstimatorKeys["ZipCode"] = "zip_code";
    CostEstimatorKeys["HybridSensor"] = "hybrid_sensor";
    CostEstimatorKeys["SensorCertification"] = "sensor_certification";
    CostEstimatorKeys["SensorProductivity"] = "sensor_productivity";
    CostEstimatorKeys["Etas"] = "etas";
    CostEstimatorKeys["Scop"] = "scop";
    CostEstimatorKeys["PacNominalPower"] = "pac_nominal_power";
    CostEstimatorKeys["CoupledWithLowTempEmitters"] = "coupled_with_low_temp_emitters";
    CostEstimatorKeys["TotalCost"] = "total_cost";
    CostEstimatorKeys["ThermodynamicWaterHeaterInstallationType"] = "thermodynamic_water_heater_installation_type";
    CostEstimatorKeys["Cop"] = "cop";
    CostEstimatorKeys["CopAccordingToEn16147Standard"] = "cop_according_to_en16147_standard";
    CostEstimatorKeys["RegulatorAmplitude"] = "regulator_amplitude";
    CostEstimatorKeys["RegulatorDrift"] = "regulator_drift";
    CostEstimatorKeys["RegulatorBrand"] = "regulator_brand";
    CostEstimatorKeys["RegulatorClass"] = "regulator_class";
    CostEstimatorKeys["RegulatorReference"] = "regulator_reference";
    CostEstimatorKeys["PresenceSensor"] = "presence_sensor";
    CostEstimatorKeys["OverconsumptionIndicator"] = "overconsumption_indicator";
    CostEstimatorKeys["HotWaterProductionOnly"] = "hot_water_production_only";
    CostEstimatorKeys["Verbose"] = "verbose";
    CostEstimatorKeys["RollerBlindsMaterial"] = "roller_blinds_material";
    CostEstimatorKeys["RollerBlindsCount"] = "roller_blinds_count";
})(CostEstimatorKeys || (CostEstimatorKeys = {}));

var EstimateOfferKey;
(function (EstimateOfferKey) {
    EstimateOfferKey["Serenity"] = "offre_serenite";
    EstimateOfferKey["Liberty"] = "offre_liberte";
})(EstimateOfferKey || (EstimateOfferKey = {}));

class CostEstimatorService {
    constructor() {
        this.estimateEndPointUrl = inject(COST_ESTIMATOR_WEB_SERVICE).estimate;
        this.httpClient = inject(HttpClient);
    }
    getCostEstimatorUrl(endpoint) {
        return `${this.estimateEndPointUrl}/${endpoint}`;
    }
    estimateAerovoltaic(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`aerovoltaic`), costEstimatorApiPayload);
    }
    estimateAirAirHeatPump(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`air_air_heat_pump`), costEstimatorApiPayload);
    }
    estimateAirWaterHeatPump(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`air_water_heat_pump`), costEstimatorApiPayload);
    }
    estimateAtticsInsulation(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`attics_insulation`), costEstimatorApiPayload);
    }
    estimateCombinedSolarSystem(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`combined_solar_system`), costEstimatorApiPayload);
    }
    estimateDoubleFlowCmv(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`double_flow_cmv`), costEstimatorApiPayload);
    }
    estimateDoubleGlazing(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`double_glazing`), costEstimatorApiPayload);
    }
    estimateElectricWaterHeater(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`electric_water_heater`), costEstimatorApiPayload);
    }
    estimateRollerBlinds(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`roller_blinds`), costEstimatorApiPayload);
    }
    estimateFireplaceInsert(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`fireplace_insert`), costEstimatorApiPayload);
    }
    estimateGeothermalHeatPump(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`geothermal_heat_pump`), costEstimatorApiPayload);
    }
    estimateGlobalRenovation(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`global_renovation`), costEstimatorApiPayload);
    }
    estimateHybridHeatPump(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`hybrid_heat_pump`), costEstimatorApiPayload);
    }
    estimatePhotovoltaicPanel(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`photovoltaic_panel`), costEstimatorApiPayload);
    }
    estimateSolarWaterHeater(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`solar_water_heater`), costEstimatorApiPayload);
    }
    estimateStorageHeater(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`storage_heater`), costEstimatorApiPayload);
    }
    estimateThermodynamicWaterHeater(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`thermodynamic_water_heater`), costEstimatorApiPayload);
    }
    estimateWoodBoiler(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`wood_boiler`), costEstimatorApiPayload);
    }
    estimateWoodStove(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`wood_stove`), costEstimatorApiPayload);
    }
    estimateRoofTerraceInsulation(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`roof_terrace_insulation`), costEstimatorApiPayload);
    }
    estimateCondensingGasBoiler(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`condensing_gas_boiler`), costEstimatorApiPayload);
    }
    estimateCondensingOilBoiler(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`condensing_oil_boiler`), costEstimatorApiPayload);
    }
    estimateFloorInsulation(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`floor_insulation`), costEstimatorApiPayload);
    }
    estimateWallInsulation(costEstimatorApiPayload) {
        return this.httpClient.post(this.getCostEstimatorUrl(`wall_insulation`), costEstimatorApiPayload);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "20.1.6", ngImport: i0, type: CostEstimatorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "20.1.6", ngImport: i0, type: CostEstimatorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "20.1.6", ngImport: i0, type: CostEstimatorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

var AccommodationTypeEnum;
(function (AccommodationTypeEnum) {
    AccommodationTypeEnum["House"] = "house";
    AccommodationTypeEnum["Apartment"] = "apartment";
    AccommodationTypeEnum["Other"] = "other";
})(AccommodationTypeEnum || (AccommodationTypeEnum = {}));

var AtticsTypeEnum;
(function (AtticsTypeEnum) {
    AtticsTypeEnum["Outdoor"] = "outdoor";
    AtticsTypeEnum["Indoor"] = "indoor";
    AtticsTypeEnum["Lost"] = "lost";
})(AtticsTypeEnum || (AtticsTypeEnum = {}));

var BoilerLocationEnum;
(function (BoilerLocationEnum) {
    BoilerLocationEnum["Wall"] = "wall";
    BoilerLocationEnum["Floor"] = "floor";
})(BoilerLocationEnum || (BoilerLocationEnum = {}));

var BoilerTypeEnum;
(function (BoilerTypeEnum) {
    BoilerTypeEnum["Wood"] = "wood";
    BoilerTypeEnum["Pellet"] = "pellet";
})(BoilerTypeEnum || (BoilerTypeEnum = {}));

var Dpe;
(function (Dpe) {
    Dpe["A"] = "A";
    Dpe["B"] = "B";
    Dpe["C"] = "C";
    Dpe["D"] = "D";
    Dpe["E"] = "E";
    Dpe["F"] = "F";
    Dpe["G"] = "G";
})(Dpe || (Dpe = {}));

var EmitterCertificationEnum;
(function (EmitterCertificationEnum) {
    EmitterCertificationEnum["CatA"] = "cat_a";
    EmitterCertificationEnum["CatB"] = "cat_b";
    EmitterCertificationEnum["CatC"] = "cat_c";
    EmitterCertificationEnum["OneStar"] = "one_star";
    EmitterCertificationEnum["TwoStars"] = "two_stars";
    EmitterCertificationEnum["ThreeStars"] = "three_stars";
    EmitterCertificationEnum["ThreeStarsEye"] = "three_stars_eye";
})(EmitterCertificationEnum || (EmitterCertificationEnum = {}));

var EquipmentCertificationEnum;
(function (EquipmentCertificationEnum) {
    EquipmentCertificationEnum["None"] = "none";
    EquipmentCertificationEnum["LabelFlammeVerte"] = "label_flamme_verte";
    EquipmentCertificationEnum["LabelFlammeVerte5Etoiles"] = "label_flamme_verte_5_etoiles";
    EquipmentCertificationEnum["LabelFlammeVerte6Etoiles"] = "label_flamme_verte_6_etoiles";
    EquipmentCertificationEnum["LabelFlammeVerte7Etoiles"] = "label_flamme_verte_7_etoiles";
    EquipmentCertificationEnum["Classe5_Nf_En_303_5"] = "classe_5_nf_en_303_5";
})(EquipmentCertificationEnum || (EquipmentCertificationEnum = {}));

var ExistingHeatingModeEnum;
(function (ExistingHeatingModeEnum) {
    ExistingHeatingModeEnum["ElectricConvector"] = "electric_convector";
    ExistingHeatingModeEnum["PanelHeaters"] = "panel_heaters";
    ExistingHeatingModeEnum["HeatingCeiling"] = "heating_ceiling";
    ExistingHeatingModeEnum["HeatingFloor"] = "heating_floor";
    ExistingHeatingModeEnum["ElectricStorageHeaters"] = "electric_storage_heaters";
    ExistingHeatingModeEnum["ElectricBoiler"] = "electric_boiler";
    ExistingHeatingModeEnum["CondensingOilBoiler"] = "condensing_oil_boiler";
    ExistingHeatingModeEnum["LowTempOilBoiler"] = "low_temp_oil_boiler";
    ExistingHeatingModeEnum["OilBoiler1989"] = "oil_boiler_1989";
    ExistingHeatingModeEnum["OilBoiler1989_2000"] = "oil_boiler_1989_2000";
    ExistingHeatingModeEnum["OilBoiler2000"] = "oil_boiler_2000";
    ExistingHeatingModeEnum["OilStove"] = "oil_stove";
    ExistingHeatingModeEnum["CondensingGasBoiler"] = "condensing_gas_boiler";
    ExistingHeatingModeEnum["LowTempGasBoiler"] = "low_temp_gas_boiler";
    ExistingHeatingModeEnum["GasBoiler1989"] = "gas_boiler_1989";
    ExistingHeatingModeEnum["GasBoiler1989_2000"] = "gas_boiler_1989_2000";
    ExistingHeatingModeEnum["GasBoiler2000"] = "gas_boiler_2000";
    ExistingHeatingModeEnum["GasRadiators"] = "gas_radiators";
    ExistingHeatingModeEnum["CondensingLpgBoiler"] = "condensing_lpg_boiler";
    ExistingHeatingModeEnum["LowTempLpgBoiler"] = "low_temp_lpg_boiler";
    ExistingHeatingModeEnum["LpgBoiler1989"] = "lpg_boiler_1989";
    ExistingHeatingModeEnum["LpgBoiler1989_2000"] = "lpg_boiler_1989_2000";
    ExistingHeatingModeEnum["LpgBoiler2000"] = "lpg_boiler_2000";
    ExistingHeatingModeEnum["LpgRadiators"] = "lpg_radiators";
    ExistingHeatingModeEnum["LpgStove"] = "lpg_stove";
    ExistingHeatingModeEnum["AirAirHeatPump"] = "air_air_heat_pump";
    ExistingHeatingModeEnum["AirWaterHeatPump"] = "air_water_heat_pump";
    ExistingHeatingModeEnum["WaterWaterHeatPump"] = "water_water_heat_pump";
    ExistingHeatingModeEnum["GeothermalHeatPump"] = "geothermal_heat_pump";
    ExistingHeatingModeEnum["HybridHeatPump"] = "hybrid_heat_pump";
    ExistingHeatingModeEnum["WoodBurningBoiler"] = "wood_burning_boiler";
    ExistingHeatingModeEnum["WoodStove"] = "wood_stove";
    ExistingHeatingModeEnum["Chimney"] = "chimney";
    ExistingHeatingModeEnum["PelletBoiler"] = "pellet_boiler";
    ExistingHeatingModeEnum["PelletStove"] = "pellet_stove";
    ExistingHeatingModeEnum["FireplaceInsert"] = "fireplace_insert";
    ExistingHeatingModeEnum["CoalFiredBoiler"] = "coal_fired_boiler";
    ExistingHeatingModeEnum["CoalStove"] = "coal_stove";
    ExistingHeatingModeEnum["LowEfficiencyElectricHeating"] = "low_efficiency_electric_heating";
})(ExistingHeatingModeEnum || (ExistingHeatingModeEnum = {}));

var ExistingHeatingTypeEnum;
(function (ExistingHeatingTypeEnum) {
    ExistingHeatingTypeEnum["ElectricBoiler"] = "electric_boiler";
    ExistingHeatingTypeEnum["ElectricHeating"] = "electric_heating";
    ExistingHeatingTypeEnum["CondensingOilBoiler"] = "condensing_oil_boiler";
    ExistingHeatingTypeEnum["NonCondensingOilBoiler"] = "non_condensing_oil_boiler";
    ExistingHeatingTypeEnum["OilHeating"] = "oil_heating";
    ExistingHeatingTypeEnum["GasHeating"] = "gas_heating";
    ExistingHeatingTypeEnum["CondensingGasBoiler"] = "condensing_gas_boiler";
    ExistingHeatingTypeEnum["NonCondensingGasBoiler"] = "non_condensing_gas_boiler";
    ExistingHeatingTypeEnum["LpgHeating"] = "lpg_heating";
    ExistingHeatingTypeEnum["CondensingLpgBoiler"] = "condensing_lpg_boiler";
    ExistingHeatingTypeEnum["NonCondensingLpgBoiler"] = "non_condensing_lpg_boiler";
    ExistingHeatingTypeEnum["AirAirHeatPump"] = "air_air_heat_pump";
    ExistingHeatingTypeEnum["AirWaterHeatPump"] = "air_water_heat_pump";
    ExistingHeatingTypeEnum["GeothermalHeatPump"] = "geothermal_heat_pump";
    ExistingHeatingTypeEnum["HybridHeatPump"] = "hybrid_heat_pump";
    ExistingHeatingTypeEnum["WoodBoiler"] = "wood_boiler";
    ExistingHeatingTypeEnum["WoodHeating"] = "wood_heating";
    ExistingHeatingTypeEnum["CoalFiredBoiler"] = "coal_fired_boiler";
    ExistingHeatingTypeEnum["CoalHeating"] = "coal_heating";
    ExistingHeatingTypeEnum["LowEfficiencyElectricHeating"] = "low_efficiency_electric_heating";
})(ExistingHeatingTypeEnum || (ExistingHeatingTypeEnum = {}));

var HeatPumpTypeEnum;
(function (HeatPumpTypeEnum) {
    HeatPumpTypeEnum["Low"] = "low";
    HeatPumpTypeEnum["Medium"] = "medium";
    HeatPumpTypeEnum["High"] = "high";
})(HeatPumpTypeEnum || (HeatPumpTypeEnum = {}));

var HeatingEnergyEnum;
(function (HeatingEnergyEnum) {
    HeatingEnergyEnum["Combustible"] = "Combustible";
    HeatingEnergyEnum["Electricity"] = "Electricit\u00E9";
})(HeatingEnergyEnum || (HeatingEnergyEnum = {}));

var HousingAgeEnum;
(function (HousingAgeEnum) {
    HousingAgeEnum["LessThan2Years"] = "less_than_2_years";
    HousingAgeEnum["Between2And15Years"] = "between_2_and_15_years";
    HousingAgeEnum["MoreThan15Years"] = "more_than_15_years";
})(HousingAgeEnum || (HousingAgeEnum = {}));

var OfferTypeEnum;
(function (OfferTypeEnum) {
    OfferTypeEnum["Liberte"] = "liberte";
    OfferTypeEnum["Serenite"] = "serenite";
})(OfferTypeEnum || (OfferTypeEnum = {}));

var OwnerStatusEnum;
(function (OwnerStatusEnum) {
    OwnerStatusEnum["ResidentOwner"] = "resident_owner";
    OwnerStatusEnum["FreeOccupant"] = "free_occupant";
    OwnerStatusEnum["Tenant"] = "tenant";
    OwnerStatusEnum["SecondaryResidenceOwner"] = "secondary_residence_owner";
    OwnerStatusEnum["LeasingOwner"] = "leasing_owner";
})(OwnerStatusEnum || (OwnerStatusEnum = {}));

var RollerBlindsMaterial;
(function (RollerBlindsMaterial) {
    RollerBlindsMaterial["Aluminium"] = "aluminium";
    RollerBlindsMaterial["Pvc"] = "pvc";
    RollerBlindsMaterial["Wood"] = "wood";
})(RollerBlindsMaterial || (RollerBlindsMaterial = {}));

var StoveTypeEnum;
(function (StoveTypeEnum) {
    StoveTypeEnum["Wood"] = "wood";
    StoveTypeEnum["Pellet"] = "pellet";
})(StoveTypeEnum || (StoveTypeEnum = {}));

var ThermodynamicWaterHeaterInstallationTypeEnum;
(function (ThermodynamicWaterHeaterInstallationTypeEnum) {
    ThermodynamicWaterHeaterInstallationTypeEnum["OtherTypesOfInstallations"] = "autres types d'installations";
    ThermodynamicWaterHeaterInstallationTypeEnum["OnExtractAir"] = "sur air extrait";
})(ThermodynamicWaterHeaterInstallationTypeEnum || (ThermodynamicWaterHeaterInstallationTypeEnum = {}));

var WallInsulationTypeEnum;
(function (WallInsulationTypeEnum) {
    WallInsulationTypeEnum["Cladding"] = "cladding";
    WallInsulationTypeEnum["Coated"] = "coated";
    WallInsulationTypeEnum["FromIndoor"] = "from_indoor";
})(WallInsulationTypeEnum || (WallInsulationTypeEnum = {}));

var WallInsulationWorkprojectInsulationTypeEnum;
(function (WallInsulationWorkprojectInsulationTypeEnum) {
    WallInsulationWorkprojectInsulationTypeEnum["Internal"] = "internal";
    WallInsulationWorkprojectInsulationTypeEnum["External"] = "external";
    WallInsulationWorkprojectInsulationTypeEnum["DoesntKnow"] = "doesnt_know";
})(WallInsulationWorkprojectInsulationTypeEnum || (WallInsulationWorkprojectInsulationTypeEnum = {}));

var WindowMaterialEnum;
(function (WindowMaterialEnum) {
    WindowMaterialEnum["Aluminium"] = "aluminium";
    WindowMaterialEnum["Mixed"] = "mixed";
    WindowMaterialEnum["Wood"] = "wood";
    WindowMaterialEnum["Pvc"] = "pvc";
})(WindowMaterialEnum || (WindowMaterialEnum = {}));

var WindowTypeEnum;
(function (WindowTypeEnum) {
    WindowTypeEnum["RoofWindow"] = "fen\u00EAtre de toiture";
    WindowTypeEnum["OtherWindow"] = "autre fen\u00EAtre ou porte fen\u00EAtre";
})(WindowTypeEnum || (WindowTypeEnum = {}));

class CostEstimatorServiceMock {
    estimateAerovoltaic(costEstimatorApiPayload) {
        return of({});
    }
    estimateAirAirHeatPump(costEstimatorApiPayload) {
        return of({});
    }
    estimateAirWaterHeatPump(costEstimatorApiPayload) {
        return of({});
    }
    estimateAtticsInsulation(costEstimatorApiPayload) {
        return of({});
    }
    estimateCombinedSolarSystem(costEstimatorApiPayload) {
        return of({});
    }
    estimateDoubleFlowCmv(costEstimatorApiPayload) {
        return of({});
    }
    estimateDoubleGlazing(costEstimatorApiPayload) {
        return of({});
    }
    estimateElectricWaterHeater(costEstimatorApiPayload) {
        return of({});
    }
    estimateRollerBlinds(costEstimatorApiPayload) {
        return of({});
    }
    estimateFireplaceInsert(costEstimatorApiPayload) {
        return of({});
    }
    estimateGeothermalHeatPump(costEstimatorApiPayload) {
        return of({});
    }
    estimateGlobalRenovation(costEstimatorApiPayload) {
        return of({});
    }
    estimateHybridHeatPump(costEstimatorApiPayload) {
        return of({});
    }
    estimatePhotovoltaicPanel(costEstimatorApiPayload) {
        return of({});
    }
    estimateSolarWaterHeater(costEstimatorApiPayload) {
        return of({});
    }
    estimateStorageHeater(costEstimatorApiPayload) {
        return of({});
    }
    estimateThermodynamicWaterHeater(costEstimatorApiPayload) {
        return of({});
    }
    estimateWoodBoiler(costEstimatorApiPayload) {
        return of({});
    }
    estimateWoodStove(costEstimatorApiPayload) {
        return of({});
    }
    estimateRoofTerraceInsulation(costEstimatorApiPayload) {
        return of({});
    }
    estimateCondensingGasBoiler(costEstimatorApiPayload) {
        return of({});
    }
    estimateCondensingOilBoiler(costEstimatorApiPayload) {
        return of({});
    }
    estimateFloorInsulation(costEstimatorApiPayload) {
        return of({});
    }
    estimateWallInsulation(costEstimatorApiPayload) {
        return of({});
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { AccommodationTypeEnum, AtticsTypeEnum, BoilerLocationEnum, BoilerTypeEnum, CostEstimatorKeys, CostEstimatorService, CostEstimatorServiceMock, Dpe, EmitterCertificationEnum, EquipmentCertificationEnum, EstimateOfferKey, ExistingHeatingModeEnum, ExistingHeatingTypeEnum, HeatPumpTypeEnum, HeatingEnergyEnum, HousingAgeEnum, OfferTypeEnum, OwnerStatusEnum, RollerBlindsMaterial, StoveTypeEnum, ThermodynamicWaterHeaterInstallationTypeEnum, WallInsulationTypeEnum, WallInsulationWorkprojectInsulationTypeEnum, WindowMaterialEnum, WindowTypeEnum };
